// capture-schema.ts — Gemini responseSchema + CaptureResult TS type for Phase 2.1.
// Kept separate so Phase 2.5 function-calling tool can import the schema directly.

// ── TypeScript result type ─────────────────────────────────────────────────────

/**
 * Structured extraction result returned by the Gemini model for a raw inbox text.
 * All optional fields may be absent when the model cannot confidently determine them.
 */
export interface CaptureResult {
  /** Clean, concise title extracted or inferred from the raw text. */
  title: string;
  /** Suggested conversion type matching InboxConversionType. */
  type: "task" | "project" | "goal" | "note" | "habit" | "event";
  /**
   * GTD classification:
   *   task      — actionable single next-action
   *   project   — multi-step outcome (2+ actions required)
   *   someday   — not-now / wishlist (park it)
   *   reference — information to keep, not act on
   */
  gtd: "task" | "project" | "someday" | "reference";
  /** ISO 8601 date YYYY-MM-DD resolved against today (relative → absolute). */
  dueDate?: string;
  /** 24-hour clock HH:MM (e.g. "14:30"). */
  time?: string;
  priority?: "low" | "medium" | "high";
  /** Estimated work duration in minutes (positive integer). */
  estimateMinutes?: number;
  /** Relevant labels / context tags (e.g. ["work", "urgent"]). */
  tags?: string[];
  // ── Goal-specific (only when type="goal") ──
  /** Goal horizon: quarter (months), year (this year), life (long-term). */
  horizon?: "quarter" | "year" | "life";
  /** Goal measure/unit, e.g. "Triệu VNĐ", "kg", "cuốn sách", "%". */
  metric?: string;
  /** Goal numeric target, e.g. 100 for "100 triệu". */
  target?: number;
  /** Short Vietnamese explanation of why this classification was chosen. */
  reason: string;
}

// ── Gemini responseSchema (JSON Schema object) ────────────────────────────────

/**
 * JSON Schema passed to Gemini via generationConfig.responseSchema.
 * Structured output guarantees parseable JSON — no brittle regex extraction.
 */
export const CAPTURE_RESPONSE_SCHEMA: Record<string, unknown> = {
  type: "object",
  required: ["title", "type", "gtd", "reason"],
  properties: {
    title: {
      type: "string",
      description: "Tiêu đề ngắn gọn, rõ ràng được trích xuất hoặc suy luận từ văn bản gốc.",
    },
    type: {
      type: "string",
      enum: ["task", "project", "goal", "note", "habit", "event"],
      description: "Loại chuyển đổi phù hợp nhất.",
    },
    gtd: {
      type: "string",
      enum: ["task", "project", "someday", "reference"],
      description: "Phân loại GTD: task=hành động đơn, project=kết quả nhiều bước, someday=chưa cần làm, reference=thông tin tham khảo.",
    },
    dueDate: {
      type: "string",
      description: "Ngày hạn định dạng YYYY-MM-DD (tuyệt đối, đã quy đổi từ ngày tương đối). Bỏ qua nếu không có.",
    },
    time: {
      type: "string",
      description: "Giờ định dạng HH:MM (24h). Bỏ qua nếu không có.",
    },
    priority: {
      type: "string",
      enum: ["low", "medium", "high"],
      description: "Mức ưu tiên: high=quan trọng & gấp, medium=bình thường, low=có thể trì hoãn.",
    },
    estimateMinutes: {
      type: "integer",
      description: "Ước tính thời lượng thực hiện (phút, số nguyên dương, vd 30). Bỏ qua nếu không rõ.",
    },
    tags: {
      type: "array",
      items: { type: "string" },
      description: "Nhãn / ngữ cảnh liên quan (tối đa 5 nhãn ngắn gọn bằng tiếng Anh hoặc tiếng Việt).",
    },
    horizon: {
      type: "string",
      enum: ["quarter", "year", "life"],
      description: "CHỈ dùng khi type=goal: tầm nhìn — quarter (trong vài tháng/một quý), year (trong năm nay), life (dài hạn/suốt đời).",
    },
    metric: {
      type: "string",
      description: "CHỈ dùng khi type=goal: chỉ số / đơn vị đo lường, vd 'Triệu VNĐ', 'kg', 'cuốn sách', '%'. Bỏ qua nếu không phải mục tiêu.",
    },
    target: {
      type: "number",
      description: "CHỈ dùng khi type=goal: con số mục tiêu cần đạt, vd 100 cho '100 triệu'. Bỏ qua nếu không rõ.",
    },
    reason: {
      type: "string",
      description: "Giải thích ngắn (1–2 câu tiếng Việt) tại sao chọn type/gtd này.",
    },
  },
  // LƯU Ý: KHÔNG dùng `additionalProperties` hay `minimum/maximum` ở đây — Gemini
  // responseSchema (subset OpenAPI) từ chối các khoá đó và trả 400. Ràng buộc giá
  // trị được xử lý ở mapCaptureResult (clamp/validate) thay vì ở schema.
};
